import { ID, UserSettings } from '@griply/types';

import { detachWrite } from '../detach-write';
import { FirestoreInstance } from '../types/firestore';
import { FirestoreCollections } from '../types/firestore-collections';
import { flattenSettings } from './update-user-settings';

export type UpdateUserFields = {
  name?: string;
  lastName?: string;
  metaSource?: string;
  settings?: Partial<UserSettings>;
};

export type UpdateUserOptions<
  TDocRef = unknown,
  TFieldValue = unknown,
  TDb = FirestoreInstance,
> = {
  userId: ID;
  fields: UpdateUserFields;
  doc: (db: NoInfer<TDb>, path: string, ...rest: string[]) => TDocRef;
  updateDoc: (
    ref: NoInfer<TDocRef>,
    data: Record<string, unknown>,
  ) => Promise<void>;
  db: TDb;
  serverTimestamp: () => TFieldValue;
  timestampFromDate: (date: Date) => unknown;
  onWriteError: (error: unknown) => void;
};

export const updateUser = async <TDocRef, TFieldValue, TDb = FirestoreInstance>(
  options: UpdateUserOptions<TDocRef, TFieldValue, TDb>,
): Promise<void> => {
  const { userId, fields, doc, updateDoc, db, serverTimestamp, onWriteError } =
    options;
  const { name, lastName, metaSource, settings } = fields;

  const userRef = doc(db, FirestoreCollections.Users, userId);
  const data: Record<string, unknown> = {};

  if (name !== undefined) {
    data.name = name;
  }

  if (lastName !== undefined) {
    data.lastName = lastName;
  }

  if (metaSource !== undefined) {
    data['user_metadata.source'] = metaSource;
  }

  if (settings) {
    Object.assign(
      data,
      flattenSettings(settings as Record<string, unknown>, 'settings'),
    );
  }

  detachWrite(
    updateDoc(userRef, {
      ...data,
      updatedAt: serverTimestamp(),
    }),
    onWriteError,
  );
};
